import React, { useRef, useEffect, useState } from "react";
import * as d3 from "d3";

const useResizeObserver = (ref) => {
  const [dimensions, setDimensions] = useState(null);
  useEffect(() => {
    const observeTarget = ref.current;
    const resizeObserver = new ResizeObserver((entries) => {
      entries.forEach((entry) => {
        setDimensions(entry.contentRect);
      });
    });
    resizeObserver.observe(observeTarget);
    return () => {
      resizeObserver.unobserve(observeTarget);
    };
  }, [ref]);

  return dimensions;
};

function SmallMultiples({
  annotations,
  casesData,
  incidentData,
  countries,
  labels,
  dimensions,
  columns = 3,
}) {
  const svgRef = useRef();
  const wrapperRef = useRef();
  const wrapDim = useResizeObserver(wrapperRef);

  let { height} = dimensions;

  useEffect(() => {
    const svg = d3.select(svgRef.current);

    d3.select(".legend").selectChildren().remove();
    svg.selectAll("*").remove();
    if (!wrapDim) return;

    let width = wrapDim.width;
    // if (width < 600) columns = 1;
    if (width < 900) columns = 2;

    // if(width < 768) columns = 2;

    height = 900;

    svg.attr("height", height).attr("width", width);

    const relaxFactor = 1.1;

    const colors = [
      "#1E2748",
      "#A89659",
      "#7EB3C6",
      "#684B7C",
      "#7E8491",
      // "#808492",
      "#60A17D",
    ];
    const curve = d3.curveCatmullRom;

    const mainGroup = svg.append("g").attr('transform', 'translate(50,0)');

    const smallWidth = width / (columns + 1);
    const smallHeight = height / Math.ceil(countries.length / columns + 1) - 10;

    var maxCases = 0;
    var maxIncidents = 0;
    countries.forEach((country) => {
      const countryMaxCases = d3.max(casesData, (d) => d[country]);
      const countryMaxIncidents = d3.max(incidentData, (d) => d[country]);

      maxCases = countryMaxCases > maxCases ? countryMaxCases : maxCases;
      maxIncidents =
        countryMaxIncidents > maxIncidents ? countryMaxIncidents : maxIncidents;
    });

    const dateExtent = d3.extent(casesData, (d) => d.date);

    var startDate = new Date(dateExtent[0].getTime());
    startDate.setDate(startDate.getDate() - 15);

    var endDate = new Date(dateExtent[1].getTime());
    endDate.setDate(endDate.getDate() + 15);

    const x = d3
      .scaleUtc()
      .domain([startDate, endDate])
      .range([0, smallWidth])
      .clamp(true);

    const xAxis = (g) =>
      g
        .attr("transform", `translate(0,${smallHeight})`)
        .call(
          d3
            .axisBottom(x)
            // .ticks(5)
            .tickFormat(d=>{
              // console.log('idem formatati: ', d)
              let formatString = "%b";
              if(d.getMonth() === 0) formatString = "'%Y"
              return d3.timeFormat(formatString)(d)})
            .tickSizeOuter(0)
        );

    const yRight = d3
      .scaleLinear()
      .domain([0, maxCases * relaxFactor])
      .range([smallHeight, 0]);

    const yAxisRight = (g) =>
      g
        .attr("transform", `translate(${smallWidth},0)`)
        .call(
          d3
            .axisRight(yRight)
            .ticks(5)
            .tickSizeOuter(0)
            .tickFormat(d3.format(".0s"))
        );

    const yLeft = d3
      .scaleLinear()
      .domain([0, maxIncidents * relaxFactor])
      .range([smallHeight, 0]);

    const yAxisLeft = (g) =>
      g
        .attr("transform", `translate(0,0)`)
        .call(d3.axisLeft(yLeft).ticks(5).tickSizeOuter(0));

    const line = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => yRight(d.value))
      .curve(curve);

    // console.log(incidentData)

    const highlightArea = d3
      .area()
      .x((d) => x(d[0]))
      .y0(yLeft(0))
      .y1((d) => yLeft(d[1]))
      .curve(curve);

    // console.log("going in with ", smallHeight, smallWidth);
    countries.forEach((d, index) => {
      var row = Math.floor(index / columns);
      var col = index % columns;
      const countryCanvas = mainGroup
        .append("g")
        .attr(
          "transform",
          `translate(${col * (smallWidth + smallWidth / 3)},${
            row * (smallHeight + smallHeight / 3)
          })`
        );

      countryCanvas.append("g").call(xAxis);
      countryCanvas.append("g").call(yAxisRight);
      countryCanvas.append("g").call(yAxisLeft);
      // if (index > 0) return;

      countryCanvas
        .append("text")
        .attr("x", smallWidth / 2)
        .attr("y", -5)
        .attr("text-anchor", "middle")
        .text(d);

      const countryIncidentData = incidentData.map((item) => {
        return [item.date, item[d]];
      });

      const countryCasesData = casesData.map((item) => {
        return { date: item.date, value: item[d] };
      });

      countryCanvas
        .append("path")
        .attr("class", `area area-country-${index}`)
        .attr("fill", colors[1])
        .attr("opacity", 0.5)
        .attr("d", highlightArea(countryIncidentData));

      countryCanvas
        .append("path")
        .attr("class", `country-line country-line-${index}`)
        .attr("fill", "none")
        .attr("stroke", colors[0])
        .style("opacity", 1)
        .attr("stroke-width", 2)
        .attr("d", line(countryCasesData));

      countryCanvas
        .append("rect")
        .attr("x", 0)
        .attr("y", 0)
        .attr("width", smallWidth)
        .attr("height", smallHeight)
        .attr("fill", "red")
        .attr("opacity", 0)
        .attr("class", "mouseover")
        .on("mousemove", (event) => {
          const dates = incidentData.map((d) => d.date);
          var mouse = d3.pointer(event);

          const xm = x.invert(mouse[0]).getTime();
          const i1 = d3.bisectLeft(dates, xm, 1);
          const i0 = i1 - 1;
          const i = xm - dates[i0] > dates[i1] - xm ? i1 : i0;

          if (i >= 0 && i < incidentData.length) {
            d3.selectAll(".hover-group").remove();

            const hoverGroup = countryCanvas
              .append("g")
              .attr("class", `hover-group hover-group-${index}`);

            hoverGroup
              .append("circle")
              .attr("fill", colors[1])
              .attr("stroke", "white")
              .attr("stroke-width", 1)
              .attr("r", 4)
              .attr("cx", x(countryIncidentData[i][0]))
              .attr("cy", yLeft(countryIncidentData[i][1]));

            hoverGroup
              .append("circle")
              .attr("fill", colors[0])
              .attr("stroke", "white")
              .attr("stroke-width", 1)
              .attr("r", 4)
              .attr("cx", x(countryCasesData[i].date))
              .attr("cy", yRight(countryCasesData[i].value));

            const incidentText = hoverGroup
              .append("g")
              .attr("transform", `translate(0,15)`);

              const incidentsString = d3.format(",.0f")(countryIncidentData[i][1]);
              const casesString = d3.format(",.0f")(countryCasesData[i].value);
              const dateString = d3.timeFormat("%b %Y")(countryCasesData[i].date)

              incidentText
              .append("text")
              .attr("x", smallWidth/2)
              .attr("text-anchor", "middle")
              .attr('font-size', 12)
              .text(`${dateString}: ${incidentsString} incidents, ${casesString} cases`);
          }
        });
    });
  }, [wrapDim]);

  return (
    <div>
      <div className="controls">
        <div className="legend"></div>
      </div>

      <div ref={wrapperRef} className="matrix-wrapper">
        <div className="chartWrapper">
          <svg ref={svgRef}></svg>
        </div>
      </div>
    </div>
  );
}

export default SmallMultiples;
